import { useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useUploadStore } from "../../store/uploadStore";
import { apiService } from "../../services/apiService";
import type { User, FileData, PlatformFile } from "../../types";

interface ActiveJobInfo {
  type: "conversion";
  jobId: string;
  pollingTimeoutId?: NodeJS.Timeout;
}

interface UseAssetBundleMonitoringProps {
  activeJobsRef: React.MutableRefObject<Map<string, ActiveJobInfo>>;
}

/**
 * Asset Bundle 모니터링 로직을 처리하는 훅
 */
export const useAssetBundleMonitoring = ({
  activeJobsRef,
}: UseAssetBundleMonitoringProps) => {
  const { user } = useAuth();
  const completeGlobal = useUploadStore((s) => s.complete);
  const userToken = (user as User)?.token;
  const userUid = (user as User)?.uid;

  const monitorAssetBundle = useCallback(
    async (
      id: string,
      storagePath: string,
      originalFilename: string,
      startTs: number
    ): Promise<void> => {
      try {
        const token = userToken;
        const userId = userUid;
        if (!userId) return;

        const afterSlash = storagePath.split("/")[1] || "";
        const uuidPart = afterSlash.split("_")[0] || "";
        const baseName = originalFilename.replace(/\.[^/.]+$/, "");

        let attemptCount = 0;
        const tryFindBundle = async (): Promise<void> => {
          // Check if job is still active
          const store = useUploadStore.getState();
          const item = store.items[id];
          if (!item || item.cancelled) {
            activeJobsRef.current.delete(id);
            return;
          }

          try {
            const list = (await apiService.getConvertedFiles(
              token || ""
            )) as FileData[] | null | undefined;

            const candidate = (list || []).find((f: FileData) => {
              const name = (f?.name || "").toLowerCase();
              const fullPath = (f?.full_path || "").toLowerCase();
              const hasUuid =
                uuidPart &&
                (name.includes(`${uuidPart.toLowerCase()}_`) ||
                  fullPath.includes(`/${uuidPart.toLowerCase()}_`));
              const hasBase =
                baseName &&
                (name.includes(baseName.toLowerCase()) ||
                  fullPath.includes(baseName.toLowerCase()));
              const isBundleExt =
                name.endsWith(".vse") ||
                name.endsWith(".bundle") ||
                name.endsWith(".zip");

              return (hasUuid || hasBase) && isBundleExt;
            });

            if (candidate) {
              const signedUrl = candidate.download_url || null;
              const gcsPath = candidate.gcs_path || candidate.full_path || null;
              const latestItem = useUploadStore.getState().items[id];
              if (latestItem?.cancelled) {
                activeJobsRef.current.delete(id);
                return;
              }

              // candidate를 platformFiles 형태로 변환
              const platformFiles: PlatformFile[] = [
                {
                  platform: "webgl",
                  public_url: signedUrl || undefined,
                  download_url: signedUrl || undefined,
                  gcs_path: gcsPath || undefined,
                  full_path: candidate.full_path || gcsPath || undefined,
                  filename: candidate.name || originalFilename,
                  original_filename: originalFilename,
                },
              ];

              console.log(
                "[useAssetBundleMonitoring] ✅ monitorAssetBundle에서 파일 발견:",
                {
                  id,
                  fileName: candidate.name,
                  signedUrl: signedUrl?.substring(0, 100) + "...",
                  gcsPath,
                  platformFilesCount: platformFiles.length,
                }
              );

              completeGlobal({
                id,
                fileUrl: signedUrl || null,
                gcsPath: gcsPath || null,
                convertedDownloadUrl: signedUrl || null,
                platformFiles,
                fileData: {
                  name: originalFilename,
                  filename: originalFilename,
                  gcs_path: gcsPath || undefined,
                  platform_files: platformFiles,
                } as FileData,
              });

              // Trigger global event
              try {
                const evt = new CustomEvent("upload:workCompleted", {
                  detail: { id, status: "completed" },
                });
                window.dispatchEvent(evt);
              } catch (_) {
                // Ignore errors
              }

              activeJobsRef.current.delete(id);
              return; // Complete
            }

            // Retry if not found (Fallback: Estimate most recently created bundle)
            attemptCount += 1;
            if (attemptCount % 3 === 0 && Array.isArray(list) && list.length > 0) {
              const recent = list
                .filter((f: FileData) => {
                  const name = (f?.name || "").toLowerCase();
                  const isBundleExt =
                    name.endsWith(".vse") ||
                    name.endsWith(".bundle") ||
                    name.endsWith(".zip");
                  const updatedMs = f?.updated
                    ? Date.parse(String(f.updated))
                    : 0;
                  return (
                    isBundleExt &&
                    (!startTs || updatedMs >= startTs - 2 * 60 * 1000)
                  );
                })
                .sort(
                  (a: FileData, b: FileData) =>
                    Date.parse(String(b.updated || 0)) -
                    Date.parse(String(a.updated || 0))
                )[0];
              if (recent) {
                const signedUrl = recent.download_url || null;

                console.log(
                  "[useAssetBundleMonitoring] ✅ monitorAssetBundle fallback에서 최근 파일 발견:",
                  {
                    id,
                    fileName: recent.name,
                    signedUrl: signedUrl?.substring(0, 100) + "...",
                  }
                );
                activeJobsRef.current.delete(id);
                return; // Complete
              }
            }

            const timeoutId = setTimeout(tryFindBundle, 3000);
            const existing = activeJobsRef.current.get(id);
            activeJobsRef.current.set(id, {
              ...existing,
              pollingTimeoutId: timeoutId,
            } as ActiveJobInfo);
          } catch (err) {
            console.error(
              "[useAssetBundleMonitoring] Asset bundle search failed:",
              err instanceof Error ? err.message : err
            );
            const timeoutId = setTimeout(tryFindBundle, 3000);
            const existing = activeJobsRef.current.get(id);
            activeJobsRef.current.set(id, {
              ...existing,
              pollingTimeoutId: timeoutId,
            } as ActiveJobInfo);
          }
        };

        tryFindBundle();
      } catch (e) {
        console.error(
          "[useAssetBundleMonitoring] Failed to start asset bundle monitoring:",
          e
        );
      }
    },
    [userToken, userUid, completeGlobal, activeJobsRef]
  );

  return { monitorAssetBundle };
};
